import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './App';
import {
  LayoutDashboard, Package, ShoppingBag, Users, Upload, Download,
  Plus, Edit2, Trash2, X, Search, Loader2, LogOut, RefreshCw, Save,
  AlertCircle, CheckCircle2, FileJson, DollarSign, Boxes, TrendingUp,
  Shield, ShieldCheck, UserPlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ------------------------- helpers -------------------------
const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n || 0));

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return s; }
};

const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded'];

// ------------------------- root -------------------------
export default function AdminDashboard() {
  const { token, logout, user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const check = async () => {
      if (!token) { setChecking(false); setIsAdmin(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(Boolean(data.is_admin));
        } else if (res.status === 403) {
          setIsAdmin(false);
          setError('Your account is not an admin. Ask an existing admin to add your email to ADMIN_EMAIL.');
        } else {
          setIsAdmin(false);
          setError('Session expired. Please sign in again.');
        }
      } catch (e) {
        setError('Could not reach admin API. Check your connection.');
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [token]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white">
        <div className="flex items-center gap-3" data-testid="admin-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Verifying admin access…</span>
        </div>
      </div>
    );
  }

  if (!token || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white px-6">
        <div className="max-w-md w-full bg-neutral-900 border border-neutral-800 rounded-2xl p-8" data-testid="admin-gate">
          <div className="flex items-center gap-2 mb-4 text-amber-400">
            <AlertCircle className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Admin sign-in required</h1>
          </div>
          <p className="text-neutral-400 text-sm mb-6" data-testid="admin-gate-msg">
            {error || 'You must be signed in as an admin to access this page. Go to the main site, sign in with an admin email, then return to /admin.'}
          </p>
          <div className="flex gap-3">
            <a
              href="/"
              className="flex-1 text-center bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium py-2.5 rounded-lg"
              data-testid="admin-gate-home"
            >
              Go to sign in
            </a>
            {token && (
              <button
                onClick={() => { logout(); window.location.reload(); }}
                className="px-4 py-2.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 rounded-lg text-sm"
                data-testid="admin-gate-logout"
              >
                Log out
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-20 backdrop-blur bg-neutral-950/80 border-b border-neutral-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-500 text-neutral-950 flex items-center justify-center font-black">T</div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">TishoFood</div>
              <div className="text-sm font-semibold" data-testid="admin-user-email">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="px-3 py-2 text-sm text-neutral-300 hover:text-white" data-testid="admin-back-to-site">
              ← Back to site
            </a>
            <button
              onClick={() => { logout(); window.location.href = '/'; }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-sm"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="bg-neutral-900 border border-neutral-800 rounded-2xl p-2" data-testid="admin-sidebar">
            <SidebarButton icon={LayoutDashboard} label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} tid="admin-tab-overview" />
            <SidebarButton icon={Package} label="Products" active={tab === 'products'} onClick={() => setTab('products')} tid="admin-tab-products" />
            <SidebarButton icon={ShoppingBag} label="Orders" active={tab === 'orders'} onClick={() => setTab('orders')} tid="admin-tab-orders" />
            <SidebarButton icon={Users} label="Customers" active={tab === 'customers'} onClick={() => setTab('customers')} tid="admin-tab-customers" />
            <SidebarButton icon={Shield} label="Admins" active={tab === 'admins'} onClick={() => setTab('admins')} tid="admin-tab-admins" />
            <SidebarButton icon={FileJson} label="Import / Export" active={tab === 'io'} onClick={() => setTab('io')} tid="admin-tab-io" />
          </nav>
        </aside>

        {/* Content */}
        <main>
          {tab === 'overview' && <OverviewTab token={token} onGoto={setTab} />}
          {tab === 'products' && <ProductsTab token={token} />}
          {tab === 'orders' && <OrdersTab token={token} />}
          {tab === 'customers' && <CustomersTab token={token} />}
          {tab === 'admins' && <AdminsTab token={token} currentUserEmail={user?.email} />}
          {tab === 'io' && <ImportExportTab token={token} />}
        </main>
      </div>
    </div>
  );
}

function SidebarButton({ icon: Icon, label, active, onClick, tid }) {
  return (
    <button
      onClick={onClick}
      data-testid={tid}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
        active ? 'bg-emerald-500 text-neutral-950 font-semibold' : 'text-neutral-300 hover:bg-neutral-800'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ------------------------- OVERVIEW -------------------------
function OverviewTab({ token, onGoto }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStats(await res.json());
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingBlock />;
  if (err) return <ErrorBlock message={err} onRetry={load} />;

  const cards = [
    { label: 'Revenue (paid)', value: fmtGBP(stats.revenue), icon: DollarSign, tid: 'stat-revenue' },
    { label: 'Total orders', value: stats.total_orders, icon: ShoppingBag, tid: 'stat-orders' },
    { label: 'Paid orders', value: stats.paid_orders, icon: CheckCircle2, tid: 'stat-paid' },
    { label: 'Pending payments', value: stats.pending_orders, icon: AlertCircle, tid: 'stat-pending' },
    { label: 'Products', value: stats.total_products, icon: Boxes, tid: 'stat-products' },
    { label: 'Customers', value: stats.total_users, icon: Users, tid: 'stat-users' },
    { label: 'Low stock', value: stats.low_stock_products, icon: TrendingUp, tid: 'stat-lowstock' },
    { label: 'Out of stock', value: stats.out_of_stock_products, icon: AlertCircle, tid: 'stat-oos' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Overview</h2>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="admin-overview-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4" data-testid={c.tid}>
            <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest">
              <c.icon className="h-3.5 w-3.5" /> {c.label}
            </div>
            <div className="text-2xl font-bold mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Recent orders</h3>
          <button onClick={() => onGoto('orders')} className="text-sm text-emerald-400 hover:text-emerald-300" data-testid="admin-overview-see-orders">
            See all →
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 uppercase tracking-widest text-left">
              <tr><th className="p-2">Order</th><th className="p-2">Customer</th><th className="p-2">Total</th><th className="p-2">Payment</th><th className="p-2">Date</th></tr>
            </thead>
            <tbody>
              {(stats.recent_orders || []).map((o) => (
                <tr key={o.order_id} className="border-t border-neutral-800">
                  <td className="p-2 font-mono text-xs">{(o.order_id || '').slice(0, 8)}…</td>
                  <td className="p-2">{o.full_name || o.email}</td>
                  <td className="p-2">{fmtGBP(o.total_amount ?? o.total)}</td>
                  <td className="p-2"><PaymentBadge status={o.payment_status} /></td>
                  <td className="p-2 text-neutral-400">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
              {(!stats.recent_orders || stats.recent_orders.length === 0) && (
                <tr><td colSpan={5} className="p-6 text-center text-neutral-500">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ------------------------- PRODUCTS -------------------------
function ProductsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // product being edited or null
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/admin/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter(
      (p) =>
        String(p.id).includes(s) ||
        (p.name || '').toLowerCase().includes(s) ||
        (p.category || '').toLowerCase().includes(s)
    );
  }, [products, q]);

  const remove = async (id) => {
    if (!window.confirm(`Delete product #${id}?`)) return;
    const res = await fetch(`${API_URL}/api/admin/products/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) load();
    else alert('Delete failed');
  };

  if (loading) return <LoadingBlock />;
  if (err) return <ErrorBlock message={err} onRetry={load} />;

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-2xl font-bold">Products <span className="text-sm text-neutral-500 font-normal">({products.length})</span></h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by id, name, category"
              className="pl-9 pr-3 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-sm w-72"
              data-testid="admin-products-search"
            />
          </div>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium rounded-lg text-sm"
            data-testid="admin-products-add"
          >
            <Plus className="h-4 w-4" /> Add product
          </button>
        </div>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 uppercase tracking-widest text-left bg-neutral-950">
              <tr>
                <th className="p-3">Image</th><th className="p-3">ID</th><th className="p-3">Name</th>
                <th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-neutral-800 hover:bg-neutral-950/60" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-2">
                    <img src={p.image} alt="" className="h-10 w-10 rounded-md object-cover bg-neutral-800" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  </td>
                  <td className="p-3 font-mono text-xs">{p.id}</td>
                  <td className="p-3 max-w-xs truncate">{p.name}</td>
                  <td className="p-3 text-neutral-400">{p.category}</td>
                  <td className="p-3">{fmtGBP(p.price)}</td>
                  <td className="p-3">
                    <StockCell product={p} />
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 rounded-md hover:bg-neutral-800" data-testid={`admin-product-edit-${p.id}`} title="Edit"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" data-testid={`admin-product-delete-${p.id}`} title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-neutral-500">No products match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || creating) && (
        <ProductFormModal
          token={token}
          product={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function StockCell({ product }) {
  const stock = product.inventory?.stock ?? product.stock ?? 0;
  const inStock = product.inStock !== false && stock > 0;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
      !inStock ? 'bg-red-500/10 text-red-400' : stock <= 10 ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'
    }`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {stock} {stock === 1 ? 'unit' : 'units'}
    </span>
  );
}

function ProductFormModal({ token, product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    id: product?.id || '',
    name: product?.name || '',
    category: product?.category || '',
    price: product?.price ?? 0,
    image: product?.image || '',
    description: product?.description || '',
    stock: product?.inventory?.stock ?? product?.stock ?? 0,
    inStock: product?.inStock !== false,
    weight_kg: product?.weight_kg ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const body = {
        name: form.name,
        category: form.category,
        price: parseFloat(form.price) || 0,
        image: form.image,
        description: form.description,
        stock: parseInt(form.stock, 10) || 0,
        inStock: Boolean(form.inStock),
      };
      if (form.weight_kg !== '' && form.weight_kg !== null) {
        body.weight_kg = parseFloat(form.weight_kg) || 0;
      }
      if (isNew && form.id) body.id = parseInt(form.id, 10) || form.id;

      const url = isNew
        ? `${API_URL}/api/admin/products`
        : `${API_URL}/api/admin/products/${product.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-product-form">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between px-6 py-4">
          <h3 className="font-semibold">{isNew ? 'Add product' : `Edit product #${product.id}`}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-neutral-800 rounded-md" data-testid="admin-product-form-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="p-3 rounded-lg bg-red-500/10 text-red-400 text-sm" data-testid="admin-product-form-error">{err}</div>}
          {isNew && (
            <Field label="ID (leave blank to auto-assign)">
              <input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} className={inputClass} data-testid="admin-form-id" />
            </Field>
          )}
          <Field label="Name" required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="admin-form-name" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category" required>
              <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass} data-testid="admin-form-category" />
            </Field>
            <Field label="Price (GBP)" required>
              <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} data-testid="admin-form-price" />
            </Field>
          </div>
          <Field label="Image URL">
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputClass} data-testid="admin-form-image" />
          </Field>
          {form.image && (
            <img src={form.image} alt="preview" className="h-24 w-24 rounded-lg object-cover bg-neutral-800" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
          )}
          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} data-testid="admin-form-description" />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Stock">
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputClass} data-testid="admin-form-stock" />
            </Field>
            <Field label="Weight (kg)">
              <input type="number" step="0.01" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className={inputClass} data-testid="admin-form-weight" />
            </Field>
            <Field label="In stock">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={form.inStock} onChange={(e) => setForm({ ...form, inStock: e.target.checked })} data-testid="admin-form-instock" />
                <span>Visible</span>
              </label>
            </Field>
          </div>
        </div>
        <div className="sticky bottom-0 bg-neutral-900 border-t border-neutral-800 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-300 hover:text-white" data-testid="admin-form-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-neutral-950 font-medium rounded-lg text-sm" data-testid="admin-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? 'Create' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass = 'w-full px-3 py-2 bg-neutral-950 border border-neutral-800 rounded-lg text-sm focus:outline-none focus:border-emerald-500';
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-neutral-500 mb-1">{label}{required && <span className="text-red-400"> *</span>}</span>
      {children}
    </label>
  );
}

// ------------------------- ORDERS -------------------------
function OrdersTab({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState({ status: '', payment_status: '', email: '' });
  const [selected, setSelected] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set('status', filter.status);
      if (filter.payment_status) params.set('payment_status', filter.payment_status);
      if (filter.email) params.set('email', filter.email);
      const res = await fetch(`${API_URL}/api/admin/orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const reconcile = async () => {
    setReconciling(true); setReconcileResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/reconcile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setReconcileResult(data);
      load(); // refresh table so newly-paid orders appear as paid
    } catch (e) {
      setReconcileResult({ error: e.message });
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-2xl font-bold">Orders <span className="text-sm text-neutral-500 font-normal">({orders.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-3 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-sm" data-testid="admin-orders-filter-status">
            <option value="">All statuses</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.payment_status} onChange={(e) => setFilter({ ...filter, payment_status: e.target.value })} className="px-3 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-sm" data-testid="admin-orders-filter-payment">
            <option value="">All payments</option>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            value={filter.email}
            onChange={(e) => setFilter({ ...filter, email: e.target.value })}
            placeholder="Search by email"
            className="px-3 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-sm"
            data-testid="admin-orders-filter-email"
          />
          <button onClick={reconcile} disabled={reconciling} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white rounded-lg text-sm" data-testid="admin-orders-sync-payments" title="Ask Stripe for the real status of every pending order and update the DB.">
            {reconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
            Sync payments
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-sm" data-testid="admin-orders-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {reconcileResult && !reconcileResult.error && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-3" data-testid="admin-reconcile-result">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            Reconciled <strong>{reconcileResult.checked}</strong> pending order{reconcileResult.checked === 1 ? '' : 's'} —
            <strong className="text-emerald-200"> {reconcileResult.updated_to_paid} newly paid</strong>,
            <strong> {reconcileResult.updated_to_failed}</strong> cancelled/expired,
            <strong> {reconcileResult.still_pending}</strong> still pending.
            {reconcileResult.errors > 0 && <span className="text-amber-300"> · {reconcileResult.errors} error{reconcileResult.errors === 1 ? '' : 's'}</span>}
          </div>
          <button onClick={() => setReconcileResult(null)} className="ml-auto text-emerald-400 hover:text-emerald-200" title="dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}
      {reconcileResult?.error && (
        <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-3" data-testid="admin-reconcile-error">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>Sync failed: {reconcileResult.error}</div>
          <button onClick={() => setReconcileResult(null)} className="ml-auto text-red-400 hover:text-red-200"><X className="h-4 w-4" /></button>
        </div>
      )}

      {err && <ErrorBlock message={err} onRetry={load} />}
      {loading ? <LoadingBlock /> : (
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500 uppercase tracking-widest text-left bg-neutral-950">
                <tr>
                  <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Items</th>
                  <th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Status</th>
                  <th className="p-3">Placed</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-neutral-800 hover:bg-neutral-950/60" data-testid={`admin-order-row-${o.order_id.slice(0, 8)}`}>
                    <td className="p-3 font-mono text-xs">{o.order_id.slice(0, 8)}…</td>
                    <td className="p-3">
                      <div>{o.full_name || '—'}</div>
                      <div className="text-neutral-500 text-xs">{o.email}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-neutral-400 text-xs">
                        {(o.items || []).slice(0, 2).map((i, idx) => (
                          <div key={idx}>{i.quantity}× {i.name}</div>
                        ))}
                        {(o.items || []).length > 2 && <div>+ {(o.items || []).length - 2} more…</div>}
                      </div>
                    </td>
                    <td className="p-3 font-medium">{fmtGBP(o.total_amount ?? o.total)}</td>
                    <td className="p-3"><PaymentBadge status={o.payment_status} /></td>
                    <td className="p-3"><StatusBadge status={o.status} /></td>
                    <td className="p-3 text-neutral-400 text-xs">{fmtDate(o.created_at)}</td>
                    <td className="p-3">
                      <button onClick={() => setSelected(o)} className="text-emerald-400 hover:text-emerald-300 text-xs" data-testid={`admin-order-view-${o.order_id.slice(0, 8)}`}>View</button>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-neutral-500">No orders match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <OrderDrawer token={token} order={selected} onClose={() => setSelected(null)} onUpdated={(u) => { setSelected(u); load(); }} />
      )}
    </div>
  );
}

function PaymentBadge({ status }) {
  const map = {
    paid: 'bg-emerald-500/10 text-emerald-400',
    pending: 'bg-amber-500/10 text-amber-400',
    failed: 'bg-red-500/10 text-red-400',
    refunded: 'bg-blue-500/10 text-blue-400',
  };
  return <span className={`text-xs px-2 py-1 rounded-md ${map[status] || 'bg-neutral-800 text-neutral-400'}`}>{status || 'unknown'}</span>;
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-neutral-700 text-neutral-200',
    processing: 'bg-blue-500/10 text-blue-400',
    shipped: 'bg-indigo-500/10 text-indigo-400',
    delivered: 'bg-emerald-500/10 text-emerald-400',
    cancelled: 'bg-red-500/10 text-red-400',
  };
  return <span className={`text-xs px-2 py-1 rounded-md ${map[status] || 'bg-neutral-800 text-neutral-400'}`}>{status || 'pending'}</span>;
}

function OrderDrawer({ token, order, onClose, onUpdated }) {
  const [status, setStatus] = useState(order.status || 'pending');
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status || 'pending');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${order.order_id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, payment_status: paymentStatus }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      onUpdated(await res.json());
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const ship = order.shipping_address || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-end" onClick={onClose} data-testid="admin-order-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-neutral-900 border-l border-neutral-800 h-full overflow-y-auto">
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Order</div>
            <div className="font-mono text-sm" data-testid="admin-order-id">{order.order_id}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-800 rounded-md" data-testid="admin-order-drawer-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Customer</div>
            <div className="font-medium">{order.full_name}</div>
            <div className="text-sm text-neutral-400">{order.email}</div>
            <div className="text-sm text-neutral-400">{order.phone || ship.phone || '—'}</div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Delivery</div>
            <div className="text-sm">
              <div className="mb-2"><span className="text-neutral-500">Method:</span> <span className="font-medium">{order.delivery_method || 'delivery'}</span></div>
              {order.delivery_method === 'pickup' && order.pickup_location ? (
                <div className="text-neutral-300">
                  {order.pickup_location.name}<br />
                  {order.pickup_location.address_line1}, {order.pickup_location.city}<br />
                  {order.pickup_location.postal_code}
                </div>
              ) : (
                <div className="text-neutral-300">
                  {ship.address_line1 || ship.address}<br />
                  {ship.city}<br />
                  {ship.postal_code || order.postal_code}<br />
                  {ship.country || 'United Kingdom'}
                  {ship.notes && <div className="mt-2 text-xs text-neutral-500 border-t border-neutral-800 pt-2">Notes: {ship.notes}</div>}
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Items ({(order.items || []).length})</div>
            <div className="space-y-2">
              {(order.items || []).map((i, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm border-b border-neutral-800 pb-2">
                  <div>
                    <div className="font-medium">{i.name}</div>
                    <div className="text-neutral-500 text-xs">{i.quantity} × {fmtGBP(i.price)}</div>
                  </div>
                  <div className="font-medium">{fmtGBP((i.price || 0) * (i.quantity || 0))}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-neutral-400"><span>Subtotal</span><span>{fmtGBP(order.subtotal)}</span></div>
              <div className="flex justify-between text-neutral-400"><span>Shipping</span><span>{fmtGBP(order.shipping_cost)}</span></div>
              <div className="flex justify-between font-semibold text-base"><span>Total</span><span>{fmtGBP(order.total_amount ?? order.total)}</span></div>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Update status</div>
            {err && <div className="p-2 rounded bg-red-500/10 text-red-400 text-xs mb-2">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Order status">
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass} data-testid="admin-order-status">
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Payment status">
                <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={inputClass} data-testid="admin-order-payment-status">
                  {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <button onClick={save} disabled={saving} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-neutral-950 font-medium rounded-lg" data-testid="admin-order-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </section>

          {order.stripe_session_id && (
            <section className="text-xs">
              <div className="uppercase tracking-widest text-neutral-500 mb-1">Stripe session</div>
              <div className="font-mono text-neutral-400 break-all">{order.stripe_session_id}</div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------- CUSTOMERS -------------------------
function CustomersTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.customers || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) => (r.email || '').toLowerCase().includes(s) || (r.full_name || '').toLowerCase().includes(s)
    );
  }, [rows, q]);

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildFilename = (ext) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const scope = q.trim() ? '-filtered' : '';
    return `tishofood-customers${scope}-${stamp}.${ext}`;
  };

  const downloadJSON = () => {
    const blob = new Blob(
      [JSON.stringify({ exported_at: new Date().toISOString(), count: filtered.length, customers: filtered }, null, 2)],
      { type: 'application/json' }
    );
    triggerDownload(blob, buildFilename('json'));
  };

  const downloadCSV = () => {
    const cols = ['email', 'full_name', 'phone', 'auth_provider', 'created_at', 'last_login_at',
                  'orders_count', 'paid_orders', 'total_spent', 'last_order_at'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\r\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(',')];
    for (const row of filtered) {
      lines.push(cols.map((c) => escape(row[c])).join(','));
    }
    // BOM so Excel opens UTF-8 correctly
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, buildFilename('csv'));
  };

  if (loading) return <LoadingBlock />;
  if (err) return <ErrorBlock message={err} onRetry={load} />;

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-2xl font-bold">Customers <span className="text-sm text-neutral-500 font-normal">({rows.length})</span></h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name"
              className="pl-9 pr-3 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-sm w-72"
              data-testid="admin-customers-search"
            />
          </div>
          <button
            onClick={downloadCSV}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 text-neutral-950 font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            data-testid="admin-customers-download-csv"
            title="Download visible customers as CSV"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
          <button
            onClick={downloadJSON}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-100 hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            data-testid="admin-customers-download-json"
            title="Download visible customers as JSON"
          >
            <FileJson className="h-4 w-4" /> JSON
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 text-sm"
            data-testid="admin-customers-refresh"
            title="Reload"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 uppercase tracking-widest text-left bg-neutral-950">
              <tr>
                <th className="p-3">Customer</th><th className="p-3">Provider</th><th className="p-3">Orders</th>
                <th className="p-3">Paid</th><th className="p-3">Lifetime spend</th><th className="p-3">Last order</th><th className="p-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => (
                <tr key={(c.id || c.email) + idx} className="border-t border-neutral-800 hover:bg-neutral-950/60" data-testid={`admin-customer-row-${(c.email || '').replace(/[^a-z0-9]/gi, '')}`}>
                  <td className="p-3">
                    <div className="font-medium">{c.full_name || '—'}</div>
                    <div className="text-neutral-500 text-xs">{c.email}</div>
                  </td>
                  <td className="p-3 text-neutral-400 text-xs">{c.auth_provider || 'password'}</td>
                  <td className="p-3">{c.orders_count}</td>
                  <td className="p-3">{c.paid_orders}</td>
                  <td className="p-3 font-medium">{fmtGBP(c.total_spent)}</td>
                  <td className="p-3 text-neutral-400 text-xs">{fmtDate(c.last_order_at)}</td>
                  <td className="p-3 text-neutral-400 text-xs">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-neutral-500">No customers match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ------------------------- ADMINS -------------------------
function AdminsTab({ token, currentUserEmail }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailToAdd, setEmailToAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load admins');
      setRows(data.admins || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const promote = async (e) => {
    e.preventDefault();
    const email = emailToAdd.trim().toLowerCase();
    if (!email) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to promote user');
      setNotice(data.message);
      setEmailToAdd('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const demote = async (email) => {
    if (!window.confirm(`Remove admin access from ${email}?`)) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/admin/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to demote admin');
      setNotice(data.message);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-admins">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-emerald-500" />
          Admins <span className="text-sm text-neutral-500 font-normal">({rows.length})</span>
        </h2>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 text-sm"
          data-testid="admins-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Promote form */}
      <form
        onSubmit={promote}
        className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4 sm:p-5"
      >
        <label className="text-sm font-semibold text-neutral-200 flex items-center gap-2 mb-2">
          <UserPlus className="h-4 w-4 text-emerald-500" /> Grant admin access
        </label>
        <p className="text-xs text-neutral-500 mb-3">
          The user must already have an account on TishoFood. Enter the exact email they signed up with.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            required
            value={emailToAdd}
            onChange={(e) => setEmailToAdd(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-emerald-500"
            data-testid="admins-email-input"
          />
          <button
            type="submit"
            disabled={busy || !emailToAdd.trim()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-neutral-950 font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            data-testid="admins-promote-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Make admin
          </button>
        </div>
      </form>

      {error && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3" data-testid="admins-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm rounded-lg p-3" data-testid="admins-notice">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {notice}
        </div>
      )}

      {/* Admins list */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/60 text-neutral-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Created</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-8 text-center text-neutral-500">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-neutral-500">No admins yet.</td></tr>
              ) : (
                rows.map((a) => {
                  const isSelf = a.email === (currentUserEmail || '').toLowerCase();
                  return (
                    <tr key={a.email} className="border-t border-neutral-800 hover:bg-neutral-950/40" data-testid={`admin-row-${a.email}`}>
                      <td className="p-3 font-mono text-neutral-100">
                        {a.email} {isSelf && <span className="ml-2 text-xs text-emerald-400">(you)</span>}
                      </td>
                      <td className="p-3 text-neutral-300">{a.full_name || '—'}</td>
                      <td className="p-3">
                        {a.source === 'env' ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                            <ShieldCheck className="h-3 w-3" /> Seed (env)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            <Shield className="h-3 w-3" /> Promoted
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-neutral-400 text-xs">{fmtDate(a.created_at)}</td>
                      <td className="p-3 text-right">
                        {a.removable && !isSelf ? (
                          <button
                            onClick={() => demote(a.email)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 disabled:opacity-50"
                            data-testid={`admin-demote-${a.email}`}
                          >
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        ) : (
                          <span className="text-xs text-neutral-600">
                            {isSelf ? 'Cannot remove self' : 'Protected'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-neutral-500">
        <strong>Seed admins</strong> come from the <code>ADMIN_EMAIL</code> env var and cannot be removed from the UI.
        To revoke them, update the env var and redeploy. <strong>Promoted admins</strong> can be added/removed here at any time.
      </p>
    </div>
  );
}

// ------------------------- IMPORT / EXPORT -------------------------
function ImportExportTab({ token }) {
  const [json, setJson] = useState('');
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const doExport = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data.products, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tishofood-products-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setResult({ ok: true, message: `Exported ${data.count} products.` });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      let payload;
      try { payload = JSON.parse(json); }
      catch { throw new Error('That is not valid JSON.'); }
      const products = Array.isArray(payload) ? payload : payload.products;
      if (!Array.isArray(products)) throw new Error('Expected an array of products, or {"products":[...]}');
      const res = await fetch(`${API_URL}/api/admin/products/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ products, replace }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult({ ok: true, message: `Imported. Inserted ${data.inserted}, updated ${data.updated}, total in DB: ${data.total_in_db}.` });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setJson(String(reader.result || ''));
    reader.readAsText(file);
  };

  // ---- Full DB backup ----
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const loadCollections = useCallback(async () => {
    setLoadingCollections(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCollections(data.collections || []);
    } catch {
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  }, [token]);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadCollection = async (name) => {
    setDownloadingKey(name); setErr(''); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/${name}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
        `tishofood-${name}-${stamp}.json`
      );
      setResult({ ok: true, message: `Downloaded ${data.count} rows from '${name}'.` });
    } catch (e) { setErr(e.message); }
    finally { setDownloadingKey(null); }
  };

  const downloadAll = async () => {
    setDownloadingKey('__all__'); setErr(''); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
        `tishofood-full-backup-${stamp}.json`
      );
      const total = Object.values(data.collections || {}).reduce((s, c) => s + (c.count || 0), 0);
      setResult({ ok: true, message: `Downloaded full backup — ${total} rows across ${Object.keys(data.collections || {}).length} collections.` });
    } catch (e) { setErr(e.message); }
    finally { setDownloadingKey(null); }
  };

  const downloadUsersCSV = async () => {
    setDownloadingKey('users-csv'); setErr(''); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const cols = ['id', 'email', 'full_name', 'phone', 'auth_provider', 'is_active',
                    'email_verified', 'is_admin', 'created_at', 'last_login_at'];
      const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\r\n]/.test(s) ? `"${s}"` : s;
      };
      const lines = [cols.join(',')];
      for (const u of (data.documents || [])) {
        lines.push(cols.map((c) => escape(u[c])).join(','));
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(
        new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }),
        `tishofood-users-${stamp}.csv`
      );
      setResult({ ok: true, message: `Downloaded ${data.count} users as CSV.` });
    } catch (e) { setErr(e.message); }
    finally { setDownloadingKey(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-io">
      <h2 className="text-2xl font-bold">Import / Export</h2>

      {/* ---- DATABASE BACKUP ---- */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6" data-testid="admin-db-backup">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="font-semibold flex items-center gap-2 text-lg">
            <Download className="h-5 w-5 text-emerald-500" /> Database backup
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadUsersCSV}
              disabled={downloadingKey === 'users-csv'}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 text-neutral-950 hover:bg-emerald-400 disabled:opacity-50 text-sm font-semibold"
              data-testid="admin-backup-users-csv"
              title="Download all users as CSV (Excel-friendly)"
            >
              {downloadingKey === 'users-csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Users CSV
            </button>
            <button
              onClick={downloadAll}
              disabled={downloadingKey === '__all__'}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 disabled:opacity-50 text-sm font-semibold"
              data-testid="admin-backup-all-json"
              title="Download the entire database as one JSON file"
            >
              {downloadingKey === '__all__' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
              Full backup (JSON)
            </button>
          </div>
        </div>
        <p className="text-sm text-neutral-400 mb-4">
          Download any collection as JSON, or grab the whole database in one file. Passwords, verification codes, and other sensitive fields are automatically scrubbed.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {loadingCollections ? (
            <div className="col-span-full text-neutral-500 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading collections…
            </div>
          ) : collections.length === 0 ? (
            <div className="col-span-full text-neutral-500 text-sm">No collections available.</div>
          ) : (
            collections.map((c) => (
              <button
                key={c.name}
                onClick={() => downloadCollection(c.name)}
                disabled={downloadingKey === c.name}
                className="text-left bg-neutral-950 border border-neutral-800 hover:border-emerald-500/50 hover:bg-neutral-900 rounded-xl p-3 transition-colors disabled:opacity-50"
                data-testid={`admin-backup-${c.name}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-neutral-100 truncate">{c.name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{c.count.toLocaleString()} rows</div>
                  </div>
                  {downloadingKey === c.name
                    ? <Loader2 className="h-4 w-4 animate-spin text-emerald-500 shrink-0" />
                    : <Download className="h-4 w-4 text-neutral-500 shrink-0" />}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {result && (
        <div className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm rounded-lg p-3">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {result.message}
        </div>
      )}
      {err && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {/* ---- PRODUCTS SYNC (kept from before) ---- */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Download className="h-4 w-4" /> Export catalog</h3>
        <p className="text-sm text-neutral-400 mb-4">Downloads every product as a JSON file. Use this on the live site, then upload the file here in preview to sync.</p>
        <button onClick={doExport} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-sm" data-testid="admin-io-export">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download products.json
        </button>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Upload className="h-4 w-4" /> Import catalog</h3>
        <p className="text-sm text-neutral-400 mb-4">Upload the JSON file exported from another environment, or paste it below. Each product must have an <code className="text-emerald-400">id</code>.</p>

        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-sm cursor-pointer" data-testid="admin-io-file-label">
            <FileJson className="h-4 w-4" /> Choose JSON file
            <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="admin-io-file" />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-neutral-300">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="admin-io-replace" />
            Replace entire catalog (deletes existing products first)
          </label>
        </div>

        <textarea
          rows={10}
          value={json}
          onChange={(e) => setJson(e.target.value)}
          placeholder='[{ "id": 1, "name": "…", "price": 9.99, ... }, ...]'
          className={inputClass + ' font-mono text-xs'}
          data-testid="admin-io-textarea"
        />

        {err && <div className="mt-3 p-3 rounded-lg bg-red-500/10 text-red-400 text-sm" data-testid="admin-io-error">{err}</div>}
        {result && <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm" data-testid="admin-io-success">{result.message}</div>}

        <div className="mt-4 flex justify-end">
          <button
            onClick={doImport}
            disabled={busy || !json.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-neutral-950 font-medium rounded-lg text-sm"
            data-testid="admin-io-import"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {replace ? 'Replace catalog' : 'Import (upsert)'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- shared -------------------------
function LoadingBlock() {
  return (
    <div className="p-12 text-center text-neutral-400" data-testid="admin-loading-block">
      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
      Loading…
    </div>
  );
}
function ErrorBlock({ message, onRetry }) {
  return (
    <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-300 flex items-center justify-between" data-testid="admin-error-block">
      <div className="flex items-center gap-2"><AlertCircle className="h-5 w-5" /> {message}</div>
      {onRetry && <button onClick={onRetry} className="text-sm underline">Retry</button>}
    </div>
  );
}
